import Grid from 'Components/Atoms/Grid/Grid';
import Title from 'Components/Atoms/Title/Title';
import EditButton from 'Components/Molecules/EditButton/EditButton';
import FormGroup from 'Components/Molecules/Form/FormGroup/FormGroup';
import WarningModal from 'Components/Molecules/Modals/Actions/WarningModal/WarningModal';
import Field from 'Pages/Dashboard/Settings/FieldsManagement/Fields/FieldTypes/Field';
import { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import EntityHelper from 'Services/Settings/Entity/EntityHelper';
import { createBusinessUnitValues, updateBusinessUnit, updateBusinessUnitValues } from 'Store/BusinessUnit/Actions';
import { createCandidateValues, updateCandidate, updateCandidateValues } from 'Store/Candidate/Actions';
import { createContactValues, updateContact, updateContactValues } from 'Store/Contact/Actions';
import { FieldType } from 'Store/FieldManagement/PropTypes';
import { StoreState } from 'Store/Reducers';
import { getEntityById } from 'Store/Settings/Entities/Selectors';
import { getFieldsAsObject } from 'Store/Settings/Fields/Selectors';
import { getValuesByEntityNameByEntityId, isEntityCreatingByEntityName, isEntityPatching, selectRecordsByIdByEntityName } from 'Store/Shared/Store/Values/Selector';
import { createVacancyValues, updateVacancy, updateVacancyValues } from 'Store/Vacancy/Actions';
import styles from './LayoutContainer.module.sass';

const LayoutContainer = ({
    layout, record_id,
    entity, record,
    fieldById, values,
    isPatching, isCreating,
    ...props
}: LayoutContainerProps) => {
    const createValues = useMemo(() => EntityHelper.createValue(props, entity?.name), [entity?.name]);
    const updateValues = useMemo(() => EntityHelper.updateValue(props, entity?.name), [entity?.name]);
    const [state, setState] = useState<any>({
        isEdit: false,
        isValid: false,
        changedPublicFields: []
    });

    const [data, setData] = useState({});

    const handleEdit = (value: any) => {
        if (!value) setData({});

        const isValid = layout
            .fields
            .map(i => fieldById?.[i])
            .filter(i => i.required)
            .every(field => {
                if (field?.type === FieldType.Address) {
                    console.log('value: ', values, field.id);
                }
                let value = values?.[field.id]?.value;
                if (!value) return false;
                if (field?.type === FieldType.Combination) {
                    let isValid = false;
                    value.forEach(i => {
                        if (isValid) return;
        
                        isValid = field?.default?.fields.every(j => i?.[j]?.length > 0);
                    });
        
                    return isValid;
                }

                if (field?.type === FieldType.Address) {
                    return value?.address?.street_number && value?.address?.route;
                }
        
                if (typeof value == 'number') {
                    return value > 0;
                }
        
                return value?.length > 0;
            });
        
        setState((state) => ({
            ...state,
            isEdit: value,
            isValid,
            changedPublicFields: []
        }));
    };

    const handleSaveValidation = () => {
        if (record?.is_published || record?.is_sponsored) {
            const publicFields = layout.fields
            ?.map(fieldId => fieldById?.[fieldId])
            ?.filter(i => i.is_public);
            
            const fields = Object.keys(data);
            const changedPublicFields = fields
            ?.map(fieldId => fieldById?.[fieldId])
            ?.filter(i => publicFields.includes(i));
            
            setState((state) => ({
                ...state,
                changedPublicFields
            }));
            
            if (changedPublicFields.length === 0) {
                handleSave();
            }

            return;
        }

        handleSave();
    }

    const handleSave = () => {
        let createValuesPayload = {};
        let updateValuesPayload = {};

        for (const fieldId of Object.keys(data)) {
            const item = data?.[fieldId];
            if (!item?.value_id) {
                createValuesPayload[fieldId] = item?.value;
                
                continue;
            }

            updateValuesPayload[item.value_id] = item.value;
        }

        const hasCreateValues = Object.keys(createValuesPayload).length > 0;
        const hasUpdateValues = Object.keys(updateValuesPayload).length > 0;

        if (hasCreateValues) createValues(record_id, createValuesPayload);
        if (hasUpdateValues) updateValues(record_id, updateValuesPayload);

        if (!hasCreateValues && !hasUpdateValues) {
            setState((state) => ({
                ...state,
                isEdit: false
            }));
        }
    }

    const handleChange = (fieldId, value) => {
        const newValues = {
            ...data,
            [fieldId]: {
                ...values?.[fieldId],
                value: value
            }
        };

        const isValid = layout
            .fields
            .map(i => fieldById?.[i])
            .filter(i => i.required)
            .every(field => {
                let value = ({
                    ...values,
                    ...newValues
                })?.[field.id]?.value;
                if (!value) return false;
                if (field?.type === FieldType.Combination) {
                    let isValid = false;
                    value.forEach(i => {
                        if (isValid) return;
        
                        isValid = field?.default?.fields.every(j => i?.[j]?.length > 0);
                    });
        
                    return isValid;
                }
        
                if (typeof value == 'number') {
                    return value > 0;
                }
        
                return value?.length > 0;
            });

        setData(newValues);
        setState((state) => ({
            ...state,
            isValid: isValid
        }));
    }

    const columns = useMemo(() => {
        let columns: any = [];

        if (layout?.attributes?.columns === 2) {
            columns.push(layout?.fields?.slice(0, Math.floor(layout?.fields?.length/2)));
            columns.push(layout?.fields?.slice(Math.floor(layout?.fields?.length/2)));
        } else {
            columns.push(layout?.fields);
        }

        return columns;
    }, [layout?.attributes?.columns]);

    if (!layout?.name) {
        return <></>;
    }

    return <div className={`
        ${styles['layoutcontainer']}
        ${styles['layoutcontainer__size--'+(layout?.attributes?.width === 2 ? 'full' : 'half')]}
    `}>
        {
            state.changedPublicFields.length > 0 &&
            <WarningModal
                onCancel={handleEdit}
                onConfirm={handleSave}
            >
                De volgende velden worden aangepast en staan live op de website. Deze aanpassingen worden direct doorgevoerd naar de website. Weet je zeker dat je deze wilt aanpassen?
                <ul>
                    { state.changedPublicFields.map(field => <li>{field?.name}</li>) }
                </ul>
            </WarningModal>
        }

        <div className={styles['layoutcontainer__header']}>
            <Grid
                position='between'
                align='center'
                axis='horizontal'
            >
                <Title weight='semibold'>
                    { layout?.name }
                </Title>
                <EditButton
                    variant='button'
                    isEdit={state.isEdit}
                    onChange={handleEdit}
                    onSave={handleSaveValidation}
                    isLoading={isPatching || isCreating}
                    isDisabled={state.isEdit && !state.isValid}
                />
            </Grid>
        </div>

        <div className={styles['layoutcontainer__content']}>
            {
                columns.map(i => {
                    return <div className={styles['layoutcontainer__content-item']}>
                        {
                            i?.map((fieldId: any) => {
                                const field = fieldById?.[fieldId];

                                if (!field?.name) {
                                    return <FormGroup title='Veld niet gevonden...'>
                                        Oops..
                                    </FormGroup>
                                }

                                return <FormGroup
                                    title={field?.name}
                                    isPublic={field?.is_public}
                                    isRequired={state.isEdit && field?.required}
                                    isTitle={entity?.name_fields?.includes(field.id)}
                                >
                                    <Field
                                        isEdit={state.isEdit}
                                        type={field.type}
                                        value={(data?.[fieldId] ?? values?.[fieldId])?.value}
                                        onChange={(value: any) => handleChange(fieldId, value)}
                                        fieldProperties={{
                                            ...field?.default,
                                            alignLeft: true
                                        }}
                                    />
                                </FormGroup>
                            })
                        }
                    </div>
                })
            }
        </div>
    </div>;
}

interface Props {
    layout: any
    record_id: any
    entity_id: any
}

const mapState = (state: StoreState, props: Props) => ({
    entity: getEntityById(state, props.entity_id),
    fieldById: getFieldsAsObject(state),


    record: selectRecordsByIdByEntityName(state, getEntityById(state, props.entity_id)?.name, props?.record_id),
    values: getValuesByEntityNameByEntityId(state, getEntityById(state, props.entity_id)?.name, props.record_id),

    isPatching: isEntityPatching(state, getEntityById(state, props.entity_id)?.name),
    isCreating: isEntityCreatingByEntityName(state, getEntityById(state, props.entity_id)?.name)
});

const mapDispatch = {
    updateVacancy,
    updateCandidate,
    updateBusinessUnit,
    updateContact,

    updateVacancyValues,
    updateCandidateValues,
    updateBusinessUnitValues,
    updateContactValues,

    createVacancyValues,
    createCandidateValues,
    createBusinessUnitValues,
    createContactValues
};

type LayoutContainerProps = Props & ReturnType<typeof mapState> & typeof mapDispatch;

export default connect(mapState, mapDispatch)(LayoutContainer);